import {inject, Injectable, Injector, NgZone, SecurityContext} from '@angular/core';
import {IConfigGlobal} from './entities/config.global';
import {TOAST_CONFIG} from './entities/base';
import {IConfigIndividual} from './entities/config.individual';
import {ToastPackage} from './config';
import {Overlay} from './overlay/overlay';
import {DomSanitizer} from '@angular/platform-browser';
import {ToastRef} from './toast-ref';
import {ComponentPortal} from './portal/portal';
import {IActiveToast} from './entities/active-toast';
import {ToastContainerDirective} from './toast-container.directive';

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  private token = inject(TOAST_CONFIG);
  private overlay = inject(Overlay);
  private _injector = inject(Injector);
  private sanitizer = inject(DomSanitizer);
  private ngZone = inject(NgZone);

  toastrConfig: IConfigGlobal;
  currentlyActive = 0;
  toasts: IActiveToast<any>[] = [];
  overlayContainer?: ToastContainerDirective;
  previousToastMessage: string | undefined;
  private index = 0;

  constructor() {
    this.toastrConfig = {
      ...this.token.default,
      ...this.token.config,
    };
    if (this.token.config.iconClasses) {
      this.toastrConfig.iconClasses = {
        ...this.token.default.iconClasses,
        ...this.token.config.iconClasses,
      };
    }
  }

  /** show toast */
  show<ConfigPayload = any>(message?: string, title?: string, override: Partial<IConfigIndividual<ConfigPayload>> = {}, type = '') {
    return this._preBuildNotification(type, message, title, this.applyConfig(override));
  }
  /** show successful toast */
  success<ConfigPayload = any>(message?: string, title?: string, override: Partial<IConfigIndividual<ConfigPayload>> = {}) {
    return this._preBuildNotification('success', message, title, this.applyConfig(override));
  }
  /** show error toast */
  error<ConfigPayload = any>(message?: string, title?: string, override: Partial<IConfigIndividual<ConfigPayload>> = {}) {
    return this._preBuildNotification('error', message, title, this.applyConfig(override));
  }
  /** show info toast */
  info<ConfigPayload = any>(message?: string, title?: string, override: Partial<IConfigIndividual<ConfigPayload>> = {}) {
    return this._preBuildNotification('info', message, title, this.applyConfig(override));
  }
  /** show warning toast */
  warning<ConfigPayload = any>(message?: string, title?: string, override: Partial<IConfigIndividual<ConfigPayload>> = {}) {
    return this._preBuildNotification('warning', message, title, this.applyConfig(override));
  }
  /**
   * Remove all or a single toast by id
   */
  clear(toastId?: number) {
    // Call every toastRef manualClose function
    for (const toast of this.toasts) {
      if (toastId !== undefined) {
        if (toast.toastId === toastId) {
          toast.toastRef.manualClose();
          return;
        }
      } else {
        toast.toastRef.manualClose();
      }
    }
  }
  /**
   * Remove and destroy a single toast by id
   */
  remove(toastId: number) {
    const found = this._findToast(toastId);
    if (!found) {
      return false;
    }
    found.activeToast.toastRef.close();
    this.toasts.splice(found.index, 1);
    this.currentlyActive = this.currentlyActive - 1;
    if (!this.toastrConfig.maxOpened || !this.toasts.length) {
      return false;
    }
    if (this.currentlyActive < this.toastrConfig.maxOpened && this.toasts[this.currentlyActive]) {
      const p = this.toasts[this.currentlyActive].toastRef;
      if (!p.isInactive()) {
        this.currentlyActive = this.currentlyActive + 1;
        p.activate();
      }
    }
    return true;
  }

  /**
   * Determines if toast message is already shown
   */
  findDuplicate(title = '', message = '', resetOnDuplicate: boolean, countDuplicates: boolean) {
    const {includeTitleDuplicates} = this.toastrConfig;

    for (const toast of this.toasts) {
      const hasDuplicateTitle = includeTitleDuplicates && toast.title === title;
      if ((!includeTitleDuplicates || hasDuplicateTitle) && toast.message === message) {
        toast.toastRef.onDuplicate(resetOnDuplicate, countDuplicates);
        return toast;
      }
    }

    return null;
  }

  /** create a clone of global config and apply individual settings */
  private applyConfig(override: Partial<IConfigIndividual> = {}): IConfigGlobal {
    return {...this.toastrConfig, ...override};
  }

  /**
   * Find toast object by id
   */
  private _findToast(toastId: number): {index: number; activeToast: IActiveToast<any>} | null {
    for (let i = 0; i < this.toasts.length; i++) {
      if (this.toasts[i].toastId === toastId) {
        return {index: i, activeToast: this.toasts[i]};
      }
    }
    return null;
  }

  /**
   * Determines the need to run inside angular's zone then builds the toast
   */
  private _preBuildNotification(
    toastType: string,
    message: string | undefined,
    title: string | undefined,
    config: IConfigGlobal
  ): IActiveToast<any> | null {
    if (config.onActivateTick) {
      return this.ngZone.run(() => this._buildNotification(toastType, message, title, config));
    }
    return this._buildNotification(toastType, message, title, config);
  }

  /**
   * Creates and attaches toast data to component
   * returns the active toast, or in case preventDuplicates is enabled the original/non-duplicate active toast.
   */
  private _buildNotification(
    toastType: string,
    message: string | undefined,
    title: string | undefined,
    config: IConfigGlobal
  ): IActiveToast<any> | null {
    if (!config.toastComponent) {
      throw new Error('toastComponent required');
    }

    // max opened and auto dismiss = true
    // if timeout = 0 resetting it would result in setting this.hideTime = Date.now(). Hence, we only want to reset timeout if there is
    // a timeout at all
    const duplicate = this.findDuplicate(
      title,
      message,
      this.toastrConfig.resetTimeoutOnDuplicate && config.timeOut > 0,
      this.toastrConfig.countDuplicates
    );
    if (((this.toastrConfig.includeTitleDuplicates && title) || message) && this.toastrConfig.preventDuplicates && duplicate !== null) {
      return duplicate;
    }

    this.previousToastMessage = message;
    let keepInactive = false;
    if (this.toastrConfig.maxOpened && this.currentlyActive >= this.toastrConfig.maxOpened) {
      keepInactive = true;
      if (this.toastrConfig.autoDismiss) {
        this.clear(this.toasts[0].toastId);
      }
    }

    const overlayRef = this.overlay.create(config.positionClass, this.overlayContainer);
    this.index = this.index + 1;
    let sanitizedMessage: string | undefined | null = message;
    if (message && config.enableHtml) {
      sanitizedMessage = this.sanitizer.sanitize(SecurityContext.HTML, message);
    }

    const toastRef = new ToastRef(overlayRef);
    const toastPackage = new ToastPackage(this.index, config, sanitizedMessage, title, toastType, toastRef);

    /** New injector that contains an instance of `ToastPackage`. */
    const providers = [{provide: ToastPackage, useValue: toastPackage}];
    const toastInjector = Injector.create({providers, parent: this._injector});

    const component = new ComponentPortal(config.toastComponent, toastInjector);
    const portal = overlayRef.attach(component, config.newestOnTop);
    toastRef.componentInstance = portal.instance;
    const ins: IActiveToast<any> = {
      toastId: this.index,
      title: title || '',
      message: message || '',
      toastRef,
      onShown: toastRef.afterActivate(),
      onHidden: toastRef.afterClosed(),
      onTap: toastPackage.onTap(),
      onAction: toastPackage.onAction(),
      portal,
    };

    if (!keepInactive) {
      this.currentlyActive = this.currentlyActive + 1;
      setTimeout(() => {
        ins.toastRef.activate();
      });
    }

    this.toasts.push(ins);
    return ins;
  }
}
